import jwt_decode from 'jwt-decode';

/**
 * JWT claims structure from the authentication token
 */
export interface TokenClaims {
  sub: string;
  email: string;
  clientid: string;
  roles: string;
  modules: string;
  clienthashid?: string;
  exp: number;
  debug: string;
  iss?: string;
  aud?: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'?: string;
}

/**
 * Extracts the token from URL query parameters
 * @returns The token string or null if not present
 */
export function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

/**
 * Decodes a JWT token and returns its claims
 * @param token The JWT token to decode
 * @returns The decoded claims or null if decoding fails
 */
export function decodeToken(token: string): TokenClaims | null {
  try {
    return jwt_decode<TokenClaims>(token);
  } catch {
    console.error('Failed to decode JWT token');
    return null;
  }
}

/**
 * Checks if a token has expired based on its claims
 * @param claims The decoded token claims
 * @returns true if the token has expired, false otherwise
 */
export function isTokenExpired(claims: TokenClaims): boolean {
  return Date.now() >= claims.exp * 1000;
}

/**
 * Removes the token parameter from the URL for security
 * This prevents the token from being shared or logged
 */
export function clearTokenFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('token');
  window.history.replaceState({}, document.title, url.pathname + url.search);
}

/**
 * Safely parses a JSON string, returning a default value on failure
 * @param jsonString The JSON string to parse
 * @param defaultValue The default value to return on parse failure
 * @returns The parsed value or the default value
 */
export function safeJsonParse<T>(jsonString: string | undefined, defaultValue: T): T {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString);
  } catch {
    return defaultValue;
  }
}
